import { useCallback, useEffect, useMemo, useState } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpRiskImportPermission } from '@internal/ksdp-permissions-common';
import { RISK_LIBRARY_PAGE_STYLES } from '../styles';
import type {
  AiGeneratedRisk,
  ApplicationRiskMatch,
  RiskCatalogueResponse,
  RiskScenarioCatalogue,
  RiskSearchFilter,
} from '../types';
import { AiGeneratedRisksTable } from './AiGeneratedRisksTable';
import { ApplicationMatchResults } from './ApplicationMatchResults';
import { RiskCatalogueTable } from './RiskCatalogueTable';
import { RiskMatrixPicker } from './RiskMatrixPicker';

type RiskLibraryTab = 'find' | 'catalogue' | 'ai-generated-risks';

function getActiveRiskLibraryTab(pathname: string): RiskLibraryTab {
  if (pathname.endsWith('/ai-generated-risks')) {
    return 'ai-generated-risks';
  }
  if (pathname.endsWith('/catalogue')) {
    return 'catalogue';
  }
  return 'find';
}

interface AiRisksResponse {
  risks?: AiGeneratedRisk[];
  count?: number;
}

interface SearchResponse {
  applications?: ApplicationRiskMatch[];
}

interface ImportRiskCatalogueResponse {
  catalogues_imported?: number;
  scenarios_imported?: number;
}

export function RiskLibraryPage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // UI-only gate; POST /catalogue/import is enforced by the backend router.
  const { allowed: canImport } = usePermission({
    permission: ksdpRiskImportPermission,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = getActiveRiskLibraryTab(location.pathname);
  const [catalogues, setCatalogues] = useState<RiskScenarioCatalogue[]>([]);
  const [lastImportedAt, setLastImportedAt] = useState<string | null>(null);
  const [lastImportedBy, setLastImportedBy] = useState<string | null>(null);
  const [aiRisks, setAiRisks] = useState<AiGeneratedRisk[]>([]);
  const [catalogueLoading, setCatalogueLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [riskFilters, setRiskFilters] = useState<
    Record<string, RiskSearchFilter>
  >({});
  const [selectedMatrixPositions, setSelectedMatrixPositions] = useState<
    Set<string>
  >(new Set());
  const [matches, setMatches] = useState<ApplicationRiskMatch[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState<string | null>(null);

  const request = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const baseUrl = await discoveryApi.getBaseUrl('risk-library');
      const response = await fetchApi.fetch(`${baseUrl}${path}`, init);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          body && typeof body.error === 'string'
            ? body.error
            : `Request failed (${response.status})`;
        throw new Error(message);
      }
      return body as T;
    },
    [discoveryApi, fetchApi],
  );

  const loadCatalogues = useCallback(async () => {
    setCatalogueLoading(true);
    try {
      const response = await request<RiskCatalogueResponse>('/catalogue');
      setCatalogues(response.catalogues ?? []);
      setLastImportedAt(response.lastImportedAt ?? null);
      setLastImportedBy(response.lastImportedBy ?? null);
      setCatalogueError(null);
    } catch (loadError) {
      setCatalogueError(
        loadError instanceof Error
          ? loadError.message
          : 'Could not load the Risk Scenario Catalogs.',
      );
    } finally {
      setCatalogueLoading(false);
    }
  }, [request]);

  const loadAiRisks = useCallback(async () => {
    setAiLoading(true);
    try {
      const response = await request<AiRisksResponse>('/ai-generated-risks');
      setAiRisks(response.risks ?? []);
      setAiError(null);
    } catch (loadError) {
      setAiError(
        loadError instanceof Error
          ? loadError.message
          : 'Could not load AI-generated risks.',
      );
    } finally {
      setAiLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadCatalogues();
  }, [loadCatalogues]);

  useEffect(() => {
    void loadAiRisks();
  }, [loadAiRisks]);

  // Every unique risk code across every imported catalogue, sorted
  // numerically -- this is the full set RiskMatrixPicker's risk-ID list and
  // the matrix-only search can reference; there is no "status" concept
  // anymore (per product decision, the catalogue is read-only and has no
  // Draft/Active/Archived lifecycle), so every known code is eligible.
  const riskCodes = useMemo(() => {
    const codes = new Set<string>();
    catalogues.forEach(catalogue =>
      catalogue.scenarios.forEach(scenario => codes.add(scenario.code)),
    );
    return [...codes].sort((left, right) =>
      left.localeCompare(right, undefined, { numeric: true }),
    );
  }, [catalogues]);

  const selectedFilters = useMemo(
    () => Object.values(riskFilters),
    [riskFilters],
  );
  const matrixOnlyFilters = useMemo(() => {
    if (selectedFilters.length > 0 || selectedMatrixPositions.size === 0) {
      return [];
    }

    return riskCodes.flatMap(code =>
      [...selectedMatrixPositions].flatMap(position => {
        const [probabilityCode, severityCode] = position.split('-');
        if (!probabilityCode || !severityCode) return [];
        return [
          {
            riskCode: code,
            probabilityCode,
            severityCode,
          },
        ];
      }),
    );
  }, [riskCodes, selectedFilters.length, selectedMatrixPositions]);
  const searchFilters = useMemo(
    () => (selectedFilters.length > 0 ? selectedFilters : matrixOnlyFilters),
    [matrixOnlyFilters, selectedFilters],
  );

  useEffect(() => {
    let current = true;

    if (searchFilters.length === 0) {
      setMatches([]);
      setMatchesError(null);
      setMatchesLoading(false);
    } else {
      setMatchesLoading(true);
      setMatchesError(null);
      void request<SearchResponse>('/applications/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ riskFilters: searchFilters }),
      })
        .then(response => {
          if (current) setMatches(response.applications ?? []);
        })
        .catch(searchError => {
          if (!current) return;
          setMatchesError(
            searchError instanceof Error
              ? searchError.message
              : 'Could not search applications by risk or matrix position.',
          );
          setMatches([]);
        })
        .finally(() => {
          if (current) setMatchesLoading(false);
        });
    }

    return () => {
      current = false;
    };
  }, [request, searchFilters]);

  const toggleRisk = (riskCode: string, selected: boolean) => {
    if (selected) setSelectedMatrixPositions(new Set());
    setRiskFilters(previous => {
      if (selected) {
        return {
          ...previous,
          [riskCode]: {
            riskCode,
            probabilityCode: null,
            severityCode: null,
          },
        };
      }
      const next = { ...previous };
      delete next[riskCode];
      return next;
    });
  };

  const setRiskPosition = (
    riskCode: string,
    probabilityCode: string,
    severityCode: string,
  ) => {
    setSelectedMatrixPositions(new Set());
    setRiskFilters(previous => ({
      ...previous,
      [riskCode]: {
        riskCode,
        probabilityCode,
        severityCode,
      },
    }));
  };

  const bulkSetPosition = (probabilityCode: string, severityCode: string) => {
    setSelectedMatrixPositions(new Set());
    setRiskFilters(previous => {
      const next = { ...previous };
      Object.entries(previous).forEach(([riskCode, filter]) => {
        if (filter.probabilityCode === null && filter.severityCode === null) {
          next[riskCode] = { ...filter, probabilityCode, severityCode };
        }
      });
      return next;
    });
  };

  const toggleMatrixPosition = (
    probabilityCode: string,
    severityCode: string,
  ) => {
    const key = `${probabilityCode}-${severityCode}`;
    setSelectedMatrixPositions(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearPositions = () => {
    setSelectedMatrixPositions(new Set());
    setRiskFilters(previous => {
      const next: Record<string, RiskSearchFilter> = {};
      Object.entries(previous).forEach(([riskCode, filter]) => {
        next[riskCode] = {
          ...filter,
          probabilityCode: null,
          severityCode: null,
        };
      });
      return next;
    });
  };

  const importCatalogue = async () => {
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const response = await request<ImportRiskCatalogueResponse>(
        '/catalogue/import',
        { method: 'POST' },
      );
      setImportResult(
        `Imported ${response.scenarios_imported ?? 0} scenarios across ${
          response.catalogues_imported ?? 0
        } catalogues.`,
      );
      await loadCatalogues();
    } catch (importErr) {
      setImportError(
        importErr instanceof Error
          ? importErr.message
          : 'Could not import the Risk Scenario Catalogs from Confluence.',
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Page themeId="tool">
      <style>{RISK_LIBRARY_PAGE_STYLES}</style>
      <Header title="Risk Library" />
      <Content>
        <div className="risk-library-container">
          <section className="risk-library-card">
            <h2>Browse risk scenarios and find affected applications</h2>
            <p>
              Browse the read-only Risk Scenario Catalogs and search current
              application risk-matrix positions by risk code or by severity and
              probability alone. Risk IDs are optional when searching by matrix
              position. AI Generated Risks are shown separately and are
              read-only.
            </p>
          </section>

          <div
            className="risk-library-tabs"
            role="tablist"
            aria-label="Risk Library sections"
          >
            <button
              className={`risk-library-tab${
                activeTab === 'find' ? ' is-active' : ''
              }`}
              type="button"
              role="tab"
              aria-selected={activeTab === 'find'}
              onClick={() => navigate('find')}
            >
              Find Applications by Risk
            </button>
            <button
              className={`risk-library-tab${
                activeTab === 'catalogue' ? ' is-active' : ''
              }`}
              type="button"
              role="tab"
              aria-selected={activeTab === 'catalogue'}
              onClick={() => navigate('catalogue')}
            >
              Risk Catalogue
            </button>
            <button
              className={`risk-library-tab${
                activeTab === 'ai-generated-risks' ? ' is-active' : ''
              }`}
              type="button"
              role="tab"
              aria-selected={activeTab === 'ai-generated-risks'}
              onClick={() => navigate('ai-generated-risks')}
            >
              AI Generated Risks Catalog
            </button>
          </div>

          <Routes>
            <Route index element={<Navigate to="find" replace />} />
            <Route
              path="find"
              element={
                <>
                  <RiskMatrixPicker
                    riskCodes={riskCodes}
                    riskFilters={riskFilters}
                    selectedMatrixPositions={selectedMatrixPositions}
                    onToggleRisk={toggleRisk}
                    onSetRiskPosition={setRiskPosition}
                    onBulkSetPosition={bulkSetPosition}
                    onToggleMatrixPosition={toggleMatrixPosition}
                    onClearPositions={clearPositions}
                  />
                  <ApplicationMatchResults
                    matches={matches}
                    loading={matchesLoading}
                    error={matchesError}
                    hasSelection={
                      selectedFilters.length > 0 ||
                      selectedMatrixPositions.size > 0
                    }
                  />
                </>
              }
            />
            <Route
              path="catalogue"
              element={
                <RiskCatalogueTable
                  catalogues={catalogues}
                  loading={catalogueLoading}
                  error={catalogueError}
                  lastImportedAt={lastImportedAt}
                  lastImportedBy={lastImportedBy}
                  importing={importing}
                  importError={importError}
                  importResult={importResult}
                  onImport={
                    canImport ? () => void importCatalogue() : undefined
                  }
                />
              }
            />
            <Route
              path="ai-generated-risks"
              element={
                <AiGeneratedRisksTable
                  risks={aiRisks}
                  loading={aiLoading}
                  error={aiError}
                />
              }
            />
          </Routes>
        </div>
      </Content>
    </Page>
  );
}
